import { useState, useEffect, useCallback } from 'react';
import { Building2, Plus, Search, Edit2, Trash2, Save, X, Check, RefreshCw, Phone, Mail, MapPin, FileText, User } from 'lucide-react';
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const EMPTY_FORM = {
  nom_societe: '', type: 'AMO', dirigeant: '', siret: '',
  dirigeant_type: '', dirigeant_pm_denomination: '', dirigeant_pm_representant: '',
  adresse: '', code_postal: '', ville: '',
  telephone: '', email: '', actif: true, notes: ''
};

export default function AmoMarManager() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [siretLookup, setSiretLookup] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/amo-mar`);
      if (r.ok) {
        const d = await r.json();
        setEntries(d.entries || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const importFromSheet = async () => {
    try {
      const r = await authFetch(`${API}/amo-mar/import-from-sheets`, { method: 'POST' });
      if (r.ok) {
        const d = await r.json();
        setImportDone(true);
        setTimeout(() => setImportDone(false), 3000);
        load();
        alert(`Import termine : ${d.imported} AMO/MAR importes, ${d.skipped} deja existants`);
      }
    } catch (e) { console.error(e); }
  };

  const saveEntry = async () => {
    setSaving(true);
    try {
      const url = editId ? `${API}/amo-mar/${editId}` : `${API}/amo-mar`;
      const method = editId ? 'PUT' : 'POST';
      const r = await authFetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (r.ok) {
        setShowForm(false);
        setEditId(null);
        setForm({ ...EMPTY_FORM });
        load();
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Supprimer cette fiche AMO/MAR ?')) return;
    try {
      await authFetch(`${API}/amo-mar/${id}`, { method: 'DELETE' });
      load();
    } catch (e) { console.error(e); }
  };

  const startEdit = (entry) => {
    setForm({ ...EMPTY_FORM, ...entry });
    setEditId(entry.id);
    setSiretLookup(entry.siret || '');
    setLookupError('');
    setShowForm(true);
  };

  const startNew = () => {
    setForm({ ...EMPTY_FORM });
    setEditId(null);
    setSiretLookup('');
    setLookupError('');
    setShowForm(true);
  };

  const lookupSiret = async () => {
    const siret = siretLookup.replace(/\s/g, '');
    if (siret.length !== 14) {
      setLookupError('Le SIRET doit contenir 14 chiffres');
      return;
    }
    setLookingUp(true);
    setLookupError('');
    try {
      const r = await authFetch(`${API}/amo-mar/lookup-siret/${siret}`);
      if (r.ok) {
        const data = await r.json();
        setForm(prev => ({
          ...prev,
          nom_societe: data.nom_societe || data.nom_commercial || data.nom_raison_sociale || prev.nom_societe,
          siret: data.siret || prev.siret,
          dirigeant: data.dirigeant || prev.dirigeant,
          dirigeant_type: data.dirigeant_type || '',
          dirigeant_pm_denomination: data.dirigeant_pm_denomination || '',
          dirigeant_pm_representant: data.dirigeant_pm_representant || '',
          adresse: data.adresse || prev.adresse,
          code_postal: data.code_postal || prev.code_postal,
          ville: data.ville || prev.ville,
        }));
        setLookupError('');
      } else {
        const err = await r.json();
        setLookupError(err.detail || 'Entreprise non trouvee');
      }
    } catch (e) {
      setLookupError(`Erreur de connexion${e?.message ? ': ' + e.message : ''}`);
    }
    setLookingUp(false);
  };

  const filtered = entries.filter(e => {
    const matchSearch = !search || e.nom_societe?.toLowerCase().includes(search.toLowerCase()) || e.dirigeant?.toLowerCase().includes(search.toLowerCase());
    const matchType = !filterType || e.type === filterType;
    return matchSearch && matchType;
  });

  return (
    <div className="space-y-6" data-testid="amo-mar-manager">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-red-500/20 rounded-xl border border-amber-500/30 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Gestion AMO / MAR</h2>
              <p className="text-amber-300">
                Registre des Assistants a Maitrise d'Ouvrage et Mandataires
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={importFromSheet} data-testid="import-amo-btn"
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm flex items-center gap-2 transition-colors">
              <RefreshCw className={`w-4 h-4 ${importDone ? 'text-green-400' : ''}`} />
              {importDone ? 'Import OK' : 'Importer depuis Sheet'}
            </button>
            <button onClick={startNew} data-testid="add-amo-btn"
              className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors">
              <Plus className="w-4 h-4" /> Ajouter AMO/MAR
            </button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-4 text-center">
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="text-2xl font-bold text-amber-400">{entries.filter(e => e.type === 'AMO').length}</div>
            <div className="text-xs text-slate-400">AMO</div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="text-2xl font-bold text-orange-400">{entries.filter(e => e.type === 'MAR').length}</div>
            <div className="text-xs text-slate-400">MAR</div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="text-2xl font-bold text-white">{entries.length}</div>
            <div className="text-xs text-slate-400">Total</div>
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par nom ou dirigeant..."
            data-testid="amo-search" className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none" />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} data-testid="amo-type-filter"
          className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none">
          <option value="">Tous</option>
          <option value="AMO">AMO</option>
          <option value="MAR">MAR</option>
        </select>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="bg-slate-800/90 border border-amber-500/30 rounded-xl p-6" data-testid="amo-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">{editId ? 'Modifier' : 'Nouvelle fiche'} AMO / MAR</h3>
            <button onClick={() => { setShowForm(false); setEditId(null); }} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          {/* SIRET Lookup */}
          <div className="mb-5 p-4 bg-gradient-to-r from-blue-900/30 to-cyan-900/30 rounded-xl border border-blue-500/30">
            <label className="block text-xs text-blue-300 font-semibold mb-2">Remplissage automatique par SIRET</label>
            <div className="flex items-center gap-2">
              <input
                data-testid="amo-siret-lookup-input"
                value={siretLookup}
                onChange={e => { setSiretLookup(e.target.value); setLookupError(''); }}
                onKeyDown={e => e.key === 'Enter' && lookupSiret()}
                placeholder="Entrez le numero SIRET (14 chiffres)"
                className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none font-mono tracking-wider"
              />
              <button
                data-testid="amo-siret-lookup-btn"
                onClick={lookupSiret}
                disabled={lookingUp || siretLookup.replace(/\s/g, '').length < 14}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {lookingUp ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Rechercher
              </button>
            </div>
            {lookupError && <p className="text-xs text-red-400 mt-2">{lookupError}</p>}
            {!lookupError && form.nom_societe && siretLookup && !lookingUp && (
              <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1"><Check className="w-3 h-3" /> Donnees recuperees pour {form.nom_societe}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Nom de la societe *</label>
              <input value={form.nom_societe} onChange={e => setForm(p => ({ ...p, nom_societe: e.target.value }))} data-testid="amo-form-nom"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Type *</label>
              <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} data-testid="amo-form-type"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none">
                <option value="AMO">AMO</option>
                <option value="MAR">MAR</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Nom du dirigeant</label>
              <input value={form.dirigeant} onChange={e => setForm(p => ({ ...p, dirigeant: e.target.value }))} data-testid="amo-form-dirigeant"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none" />
            </div>
            {form.dirigeant_type === 'personne_morale' && (
              <div className="col-span-2 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <label className="block text-xs text-blue-300 font-semibold mb-2">
                  {form.dirigeant_pm_denomination || 'La societe dirigeante'} est representee par :
                </label>
                <input
                  value={form.dirigeant_pm_representant}
                  onChange={e => setForm(p => ({ ...p, dirigeant_pm_representant: e.target.value }))}
                  data-testid="amo-form-pm-representant"
                  placeholder="Nom et prenom du representant legal"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
            )}
            <div>
              <label className="block text-xs text-slate-400 mb-1">N SIRET</label>
              <input value={form.siret} onChange={e => setForm(p => ({ ...p, siret: e.target.value }))} data-testid="amo-form-siret"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-slate-400 mb-1">Adresse</label>
              <input value={form.adresse} onChange={e => setForm(p => ({ ...p, adresse: e.target.value }))} data-testid="amo-form-adresse"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Code postal</label>
              <input value={form.code_postal} onChange={e => setForm(p => ({ ...p, code_postal: e.target.value }))} data-testid="amo-form-cp"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Ville</label>
              <input value={form.ville} onChange={e => setForm(p => ({ ...p, ville: e.target.value }))} data-testid="amo-form-ville"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Telephone</label>
              <input value={form.telephone} onChange={e => setForm(p => ({ ...p, telephone: e.target.value }))} data-testid="amo-form-tel"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Email</label>
              <input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} data-testid="amo-form-email"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-slate-400 mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} data-testid="amo-form-notes"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none resize-none" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.actif} onChange={e => setForm(p => ({ ...p, actif: e.target.checked }))} id="amo-actif" data-testid="amo-form-actif" />
              <label htmlFor="amo-actif" className="text-sm text-white">Actif</label>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button onClick={saveEntry} disabled={saving || !form.nom_societe} data-testid="amo-form-save"
              className="px-6 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-600 text-white rounded-lg font-medium flex items-center gap-2 transition-colors">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Enregistrement...' : editId ? 'Mettre a jour' : 'Creer'}
            </button>
            <button onClick={() => { setShowForm(false); setEditId(null); }} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">Annuler</button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-slate-400 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-slate-500 py-8">
          {entries.length === 0 ? "Aucun AMO/MAR enregistre. Cliquez sur 'Importer depuis Sheet' pour commencer." : 'Aucun resultat pour cette recherche.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {filtered.map(entry => (
            <div key={entry.id} data-testid={`amo-card-${entry.id}`}
              className={`bg-slate-800/60 border rounded-xl p-4 flex items-center gap-4 transition-colors ${entry.actif !== false ? 'border-slate-700 hover:border-amber-500/30' : 'border-slate-800 opacity-60'}`}>
              {/* Type badge */}
              <div className={`px-3 py-1 rounded-lg text-xs font-bold ${entry.type === 'AMO' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'}`}>
                {entry.type}
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-white truncate">{entry.nom_societe}</div>
                <div className="flex items-center gap-4 text-xs text-slate-400 mt-1">
                  {entry.dirigeant && <span className="flex items-center gap-1"><User className="w-3 h-3" />{entry.dirigeant}</span>}
                  {entry.dirigeant_pm_representant && <span className="flex items-center gap-1 text-blue-400">repr. par {entry.dirigeant_pm_representant}</span>}
                  {entry.telephone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{entry.telephone}</span>}
                  {entry.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{entry.email}</span>}
                  {entry.ville && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{entry.ville}</span>}
                  {entry.siret && <span className="text-slate-500">SIRET: {entry.siret}</span>}
                </div>
              </div>
              {/* Actions */}
              <div className="flex items-center gap-2">
                <button onClick={() => startEdit(entry)} data-testid={`amo-edit-${entry.id}`}
                  className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors">
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => deleteEntry(entry.id)} data-testid={`amo-delete-${entry.id}`}
                  className="p-2 hover:bg-red-900/30 rounded-lg text-slate-400 hover:text-red-400 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
